import { useEffect, useRef, useState } from "react";
import Style from "./style.module.css"
import Select from "react-select";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { CheckCircle, List, RefreshCcw, Trash2, UploadCloud, XCircle } from "lucide-react";
import { useApiCall } from "../../custom-hooks/api-call";
import FileTypeIcon from "../../icons/file_type_icon";
import { CustomDropdown } from "../../ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../../ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../../ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../../ui/dropdown/CustomDropdownMenuList";

export function FormArea({ children, className, ...props }) {
    return (
        <div className={`${Style.formArea} ${className || ''}`} {...props}>
            {children}
        </div>
    );
}

export function FormLineBreak({ children, className, ...props }) {
    return (
        <div className={`${Style.lineBreak} ${className || ''}`} {...props}>
            {children}
        </div>
    );
}

export function FormLabel({ children, className, required = false, ...props }) {
    return (
        <label className={`${Style.FormLabel} ${className || ''}`} {...props}>
            {children}

            {
                required ? (
                    <span>*</span>
                ) : null
            }
        </label>
    );
}

export function FeildWraper({ children, className, width = '50%', ...props }) {
    return (
        <div
            className={`${Style.feildWraper} ${className || ''}`}
            style={{
                flex: `0 0 ${width}`,
                width: `${width}`
            }}
            {...props}
        >
            {children}
        </div>
    );
}

export function FeildError({ children, className, ...props }) {
    return (
        <span
            className={`${Style.feildError} ${className || ''}`}
            {...props}
        >
            {children}
        </span>
    );
}

export function SelectSearch({
    options = [],
    placeholder = "Type to search...",
    value,
    onChange = () => { },
    multiple = false,
    className = "",
    isDisabled,
    menuPlacement = "auto",
    menuPosition = "absolute",
    isClearable = true
}) {
    // Convert value to react-select format
    const formattedValue = multiple
        ? Array.isArray(value)
            ? value.map((v) => options.find((o) => o.value === v) || { value: v, label: v })
            : []
        : options.find((o) => o.value === value) || null;

    const handleChange = (selected) => {
        if (multiple) {
            if (!selected) {
                onChange([]);
                return;
            }

            const lastSelected = selected[selected.length - 1];
            const isSelectAllClicked = lastSelected?.value === "__all__";

            if (isSelectAllClicked) {
                const allValues = options.map((o) => o.value);
                const isAllSelected = value?.length === options.length;
                onChange(isAllSelected ? [] : allValues); // toggle select all
            } else {
                onChange(selected.map((s) => s.value));
            }
        } else {
            onChange(selected ? selected.value : null);
        }
    };

    const selectEle = useRef();

    // Add "Select All" pseudo-option (only for multi)
    const extendedOptions = multiple
        ? [{ value: "__all__", label: "Select All" }, ...options]
        : options;



    return (
        <Select
            ref={selectEle}
            isMulti={multiple}
            placeholder={placeholder}
            value={formattedValue}
            onChange={handleChange}
            menuPlacement={menuPlacement}
            menuPosition={menuPosition} // default is "absolute", can also use "fixed"
            menuPortalTarget={typeof window !== "undefined" && menuPosition === "fixed" ? document.body : undefined}
            isDisabled={isDisabled}
            options={extendedOptions}
            isClearable={isClearable}
            className={`${Style.selectSearch} ${className}`}
            components={{ IndicatorSeparator: () => null }}
            styles={{
                control: (base, state) => ({
                    ...base,
                    minHeight: "35px",
                    height: "auto",
                    borderRadius: "5px",
                    border: "1px solid #DFDFDF",
                    backgroundColor: state.isDisabled ? "#f0f0f0" : "#FFF",
                    boxShadow: state.isFocused ? "none" : "none",
                    "&:hover": { borderColor: "#DFDFDF" },
                    fontSize: "14px",
                    lineHeight: "1.2",
                    fontWeight: 500,
                    color: "#1E1E1E",
                    padding: "0 14px",
                    cursor: state.isDisabled ? "not-allowed" : "normal", // cursor change
                }),
                valueContainer: (base) => ({
                    ...base,
                    height: "auto",
                    padding: "4px 0px 2px",
                }),
                input: (base) => ({
                    ...base,
                    margin: 0,
                    padding: 0,
                    fontSize: "14px",
                    lineHeight: "1.2",
                    fontWeight: 500,
                    color: "#1E1E1E",
                    height: 'auto'
                }),
                singleValue: (base) => ({
                    ...base,
                    fontSize: "14px",
                    lineHeight: "1.2",
                    fontWeight: 500,
                    color: "#1E1E1E",
                }),
                placeholder: (base) => ({
                    ...base,
                    fontSize: "14px",
                    lineHeight: "1.2",
                    fontWeight: 500,
                    color: "#717680",
                }),
                menuPortal: (base) => ({
                    ...base,
                    zIndex: menuPosition === "absolute" ? 1 : 9999,
                }),
                menu: (base) => ({
                    ...base,
                    marginTop: "2px",
                    borderRadius: "5px",
                    border: "1px solid #DFDFDF",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }),
                option: (base, state) => ({
                    ...base,
                    fontSize: "14px",
                    lineHeight: "1.2",
                    fontWeight: 500,
                    color: state.isDisabled
                        ? "#A0A0A0" // greyed out text for disabled
                        : state.isSelected
                            ? "#FFF"
                            : "#1E1E1E",
                    backgroundColor: state.isDisabled
                        ? "#F9F9F9" // light gray background for disabled
                        : state.isSelected
                            ? "#3b82f6"
                            : state.isFocused
                                ? "#F3F4F6"
                                : "#FFF",
                    padding: "8px 14px",
                    cursor: state.isDisabled ? "not-allowed" : "pointer",
                    opacity: state.isDisabled ? 0.8 : 1, // subtle fade for disabled options
                }),
                indicatorContainer: (base) => ({ ...base, padding: 0 }),
                dropdownIndicator: (base) => ({ ...base, padding: 0 }),
            }}
        />
    );
}

export function TagInput({ value, onChange, required, placeholder }) {
    const [input, setInput] = useState("");

    const addTag = (e) => {
        if (e.key === "Enter" && input.trim()) {
            onChange([...value, input.trim()]);
            setInput("");
            e.preventDefault();
        }
    };

    const removeTag = (tag) => {
        onChange(value.filter((t) => t !== tag));
    };

    return (
        <div className={Style.tagInput}>
            {value.map((tag, i) => (
                <span
                    key={i}
                    className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs flex items-center gap-1"
                >
                    {tag}
                    <span
                        onClick={() => removeTag(tag)}
                        className="text-blue-600 hover:text-red-500 cursor-pointer"
                    >
                        ✕
                    </span>
                </span>
            ))}
            <input
                type="text"
                placeholder={`${placeholder ? placeholder : 'Search and add'}`}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={addTag}
                className="flex-1 text-sm p-1 focus:outline-none"
            />
        </div>
    );
}

export function FormCheckbox({
    label,
    checked,
    onChange = () => { },
    disabled = false,
    className = "",
    required = false,
    name,
    value,
    ...props
}) {
    return (
        <label
            className={`${Style.formCheckbox} ${disabled ? Style.disabled : ""} ${className}`}
            htmlFor={name}
        >
            <input
                id={name}
                type="checkbox"
                name={name}
                value={value}
                checked={!!checked}
                onChange={(e) => {
                    if (typeof onChange === "function") {
                        // Pass full event for Formik compatibility
                        onChange(e);
                    }
                }}
                disabled={disabled}
                {...props}
            />
            <b></b>
            <em>
                {label}
                {required ? <span>*</span> : null}
            </em>
        </label>
    );
}

export function FormRadio({
    label,
    name,
    value,
    checked,
    onChange = () => { },
    disabled = false,
    className = "",
    required = false,
    ...props
}) {
    return (
        <label
            className={`${Style.formRadio} ${disabled ? Style.disabled : ""} ${className}`}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={checked}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                {...props}
            />
            <b></b>
            <em>
                {label}
                {required ? <span>*</span> : null}
            </em>
        </label>
    );
}

export function TextEditor({ value, onChange, placeholder, extraClass = "" }) {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Placeholder.configure({
                placeholder: placeholder || 'Start typing...',
                showOnlyWhenEditable: true,   // false if you want placeholder even in readOnly mode
                includeChildren: true,
            }),
        ],
        content: value,
        onUpdate({ editor }) {
            onChange(editor.getHTML());
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            const isFocused = editor.isFocused;
            editor.commands.setContent(value || '');
            if (isFocused) editor.commands.focus();
        }
    }, [value, editor]);

    if (!editor) return null;

    return (
        <div className={`${Style.textEditorWrap} ${extraClass}`}>
            <div className={Style.text_editor_toolbar}>
                <span onClick={() => editor.chain().focus().toggleBold().run()}>B</span>
                <span onClick={() => editor.chain().focus().toggleItalic().run()}>I</span>
                <span onClick={() => editor.chain().focus().toggleUnderline?.().run()}>U</span>
                {/* <span onClick={() => editor.chain().focus().toggleBulletList().run()}>• List</span>
                <span onClick={() => editor.chain().focus().toggleOrderedList().run()}>1. List</span> */}
                <CustomDropdown
                    position="bottom-left"
                    className="myDropdown"
                >
                    <CustomDropdownButton arrow={false} className="!bg-transparent !p-0 !border-0 !text-[12px]">
                        <List className="!w-[18px] !h-[18px] !text-black" />
                    </CustomDropdownButton>

                    <CustomDropdownMenu className="w-full">
                        <CustomDropdownMenuList onClick={() => { editor.chain().focus().toggleBulletList().run() }}>
                            <span>Bullet List</span>
                        </CustomDropdownMenuList>
                        <CustomDropdownMenuList onClick={() => { editor.chain().focus().toggleOrderedList().run() }}>
                            <span>Number List</span>
                        </CustomDropdownMenuList>
                    </CustomDropdownMenu>
                </CustomDropdown>
                <span
                    onClick={() => {
                        const url = prompt('Enter URL');
                        if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                    }}
                >
                    Link
                </span>
            </div>

            <div className={Style.text_editor_content}>
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}

export const FileUploadProgressBar = ({
    mode = "single", // "single" | "multi" | "folder"
    acceptType = "all", // "all" | "image" | "pdf"
    onChange = () => { },
    isReturnFile = false
}) => {
    const apiCall = useApiCall();
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const getReadableFileSize = (size) => {
        const units = ["B", "KB", "MB", "GB"];
        let i = 0;
        while (size >= 1024 && i < units.length - 1) {
            size /= 1024;
            i++;
        }
        return `${size.toFixed(1)} ${units[i]}`;
    };

    const getFileStatus = (file) => {
        if (file.failed) return "failed";
        if (file.progress === 100) return "complete";
        return "uploading";
    };

    const FileUpload = {
        Root: ({ children }) => <div className="flex flex-col gap-4 w-full">{children}</div>,

        DropZone: ({ onDropFiles, accept, mode }) => {
            const inputRef = useRef();

            const handleFileInput = (e) => {
                if (isReturnFile) {
                    onChange(e.target.files[0]);
                } else {
                    const files = e.target.files;
                    onDropFiles(files);
                    e.target.value = "";
                }
            };

            const handleDrop = (e) => {
                e.preventDefault();
                if (isReturnFile) {
                    onChange(e.dataTransfer.files[0])
                } else {
                    const files = e.dataTransfer.files;
                    onDropFiles(files);
                }
            };

            return (
                <label
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className={`flex flex-col !items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 
                        rounded-2xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition`}
                >
                    <UploadCloud className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-gray-600 font-medium text-sm">Drag & Drop files here!</p>
                    <p className="text-xs text-gray-400">or click to browse</p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={accept}
                        multiple={mode === "multi"}
                        className="hidden"
                        onChange={handleFileInput}
                    />
                </label>
            );
        },

        List: ({ children }) => <div className="flex flex-col gap-2">{children}</div>,

        ListItemProgressBar: ({ name, size, progress, status, type, onDelete, onRetry }) => {
            const getStatusColor = () => {
                if (status === "failed") return "text-red-600";
                if (status === "complete") return "text-green-600";
                return "text-gray-600";
            };

            const getProgressBarColor = () => {
                if (status === "failed") return "bg-red-600";
                if (status === "complete") return "bg-green-600";
                return "bg-purple-600";
            };

            return (
                <div
                    className={`flex items-center justify-between bg-white p-2 rounded-lg border ${status === "failed" ? "border-red-400" : "border-gray-200"
                        }`}
                >
                    <div className="flex items-center gap-3 w-full">
                        {/* File Icon */}
                        <div className="w-8 h-8 flex items-center justify-center">
                            <FileTypeIcon
                                type={
                                    type.includes("pdf")
                                        ? "pdf"
                                        : type.startsWith("image")
                                            ? "image"
                                            : "text"
                                }
                            />
                        </div>

                        {/* File Info */}
                        <div className="flex-1 flex flex-col">
                            <div className="flex justify-between">
                                <div className="">
                                    <span className="text-sm font-medium text-gray-800 pb-1">{name}</span>

                                    <div className="flex pb-1">
                                        <span className="text-xs text-gray-500 mt-0.5">{getReadableFileSize(size)}</span>
                                        <div className="w-px h-[12px] bg-gray-300 m-auto mx-2"></div>
                                        <span className={`text-sm ${getStatusColor()} mt-0.5`}>
                                            {status === "uploading" ?
                                                <>
                                                    <span className="flex gap-1 text-xs">
                                                        <UploadCloud size={"1em"} />
                                                        Uploading…
                                                    </span>
                                                </> :
                                                status === "complete" ?
                                                    <>
                                                        <span className="flex gap-1 items-center text-xs">
                                                            <CheckCircle size={"1em"} />
                                                            Complete
                                                        </span>
                                                    </>
                                                    :
                                                    <>
                                                        <span className="flex gap-1 items-center text-sm">
                                                            <XCircle size={"1em"} />
                                                            Failed
                                                        </span>
                                                    </>
                                            }
                                        </span>
                                    </div>
                                </div>

                                <div className="flex-shrink-0">
                                    <button onClick={onDelete} className="text-gray-400 hover:text-gray-700">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>

                            {status === "failed" ? (
                                <button
                                    onClick={onRetry}
                                    className="text-red-500 hover:text-red-700 text-xs font-medium flex items-center gap-1"
                                >
                                    <RefreshCcw size={16} /> Try again
                                </button>
                            ) : (
                                <div className="relative w-full h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                                    <div
                                        className={`absolute top-0 left-0 h-full ${getProgressBarColor()} transition-all`}
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    {/* <div className="flex items-center ml-3">
                        {status === "failed" ? (
                            <button
                                onClick={onRetry}
                                className="text-red-500 hover:text-red-700 text-xs font-medium flex items-center gap-1"
                            >
                                <RefreshCcw size={16} /> Try again
                            </button>
                        ) : (
                            <button onClick={onDelete} className="text-gray-400 hover:text-gray-700">
                                <Trash2 size={18} />
                            </button>
                        )}
                    </div> */}
                </div>
            );
        },
    };

    const uploadFile = async (file, onProgress) => {
        const formData = new FormData();
        formData.append("file", file); // must be raw File

        const url = file.type.includes("pdf") ? "upload-pdf" : "upload";

        try {
            const response = await apiCall({
                method: "post",
                url,
                data: formData,
                onUploadProgress: (event) => {
                    if (event.total) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        onProgress(progress);
                    }
                },
            });

            const uploadedPath = response?.path?.filepath;
            if (!uploadedPath) throw new Error("Invalid response path");
            return uploadedPath;
        } catch (err) {
            console.error("Upload Error:", err.response?.data || err.message);
            throw err;
        }
    };

    const handleDropFiles = (files) => {
        if (!files || files.length === 0) return;

        const newFiles = Array.from(files).map((file) => ({
            id: Math.random().toString(),
            name: file.name,
            size: file.size,
            type: file.type,
            progress: 0,
            failed: false,
            url: null,
            rawFile: file,
            status: "uploading",
        }));

        // ✅ Add new files correctly (safe state update)
        setUploadedFiles((prev) => mode === "multi" ? [...newFiles, ...prev] : [newFiles[0]]);

        newFiles.forEach(async (file) => {
            try {
                const uploadedPath = await uploadFile(file.rawFile, (progress) => {
                    setUploadedFiles((prev) =>
                        prev.map((f) =>
                            f.id === file.id
                                ? { ...f, progress, status: progress === 100 ? "complete" : "uploading" }
                                : f
                        )
                    );
                });

                // ✅ On success — update status & trigger onChange with all completed URLs
                setUploadedFiles((prev) => {
                    const updated = prev.map((f) =>
                        f.id === file.id
                            ? { ...f, progress: 100, status: "complete", url: uploadedPath }
                            : f
                    );

                    const completedUrls = updated
                        .filter((f) => f.status === "complete")
                        .map((f) => f.url);

                    onChange(mode === "multi" ? completedUrls : uploadedPath);

                    return updated;
                });

            } catch {
                setUploadedFiles((prev) =>
                    prev.map((f) =>
                        f.id === file.id ? { ...f, failed: true, status: "failed", progress: 0 } : f
                    )
                );
            }
        });
    };

    const handleDeleteFile = (id) => setUploadedFiles((prev) => prev.filter((f) => f.id !== id));

    const handleRetryFile = (id) => {
        const file = uploadedFiles.find((f) => f.id === id);
        if (file) handleDropFiles([file.rawFile]);
    };

    const getAcceptType = () => {
        if (acceptType === "image") return "image/*";
        if (acceptType === "jpg") return "image/jpg";
        if (acceptType === "pdf") return "application/pdf";
        return "*/*";
    };

    return (
        <FileUpload.Root>
            <FileUpload.DropZone onDropFiles={handleDropFiles} accept={getAcceptType()} mode={mode} />
            <FileUpload.List>
                {uploadedFiles.map((file) => {
                    return (
                        <FileUpload.ListItemProgressBar
                            key={file.id}
                            {...file}
                            status={getFileStatus(file)}
                            onDelete={() => handleDeleteFile(file.id)}
                            onRetry={() => handleRetryFile(file.id)}
                        />
                    )
                })}
            </FileUpload.List>
        </FileUpload.Root>
    );
};

export const FormSubmitButton = ({
    label = "Submit",
    loading = false,
    disabled = false,
    className = "",
    variant = "primary",
    onClick,
    ...props
}) => {
    const base = "flex items-center justify-center gap-1 px-[20px] py-[12px] rounded-[8px] text-[16px] font-medium tracking-[0.04em] leading-none shadow-[0px_1px_2px_rgba(10,13,20,0.03)] transition-all duration-300 ease-in-out w-fit disabled:opacity-50 disabled:cursor-not-allowed";

    const variants = {
        default: "border border-[#1E1E1E] bg-white text-[#1E1E1E]",
        primary: "border border-[#0B54FF] bg-[#0B54FF] text-white hover:bg-[#2c50df]",
        secondary: "border border-[#6B7280] bg-[#6B7280] text-white hover:bg-[#4B5563]",
        outline: "border border-[#0B54FF] bg-white text-[#0B54FF] hover:bg-[#0B54FF] hover:text-white",
        danger: "border border-[#DC2626] bg-[#DC2626] text-white hover:bg-[#B91C1C]",
        success: "border border-[#16A34A] bg-[#16A34A] text-white hover:bg-[#15803D]",
        ghost: "border border-transparent bg-transparent text-[#0B54FF] hover:bg-[#0B54FF]/10"
    };

    return (
        <button
            type="submit"
            onClick={onClick}
            disabled={disabled || loading}
            className={`${base} ${variants[variant]} ${className} `}
            {...props}
        >
            {loading ? "Please wait..." : label}
        </button>
    );
};








export function FormHeading1({ children, className, ...props }) {
    return (
        <h3 className={`${Style.heading1} ${className || ''}`} {...props}>
            {children}
        </h3>
    )
}

export function FormSubHeading({ children, className, ...props }) {
    return (
        <p className={`${Style.formSubHeading} ${className || ''}`} {...props}>
            {children}
        </p>
    )
}